import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  styled,
  Popover,
  Chip,
  createTheme,
  ThemeProvider,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Radio,
  RadioGroup,
} from "@mui/material";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import {
  Add,
  CancelOutlined,
  Check,
  Clear,
  Delete,
  Save,
  RestartAlt,
  Edit,
  Cancel,
  EditNote,
  BlockOutlined,
  AccessTime,
  SwapHoriz,
  PlaylistAdd,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { PatientAvailabilityDto } from "../../../api-client";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import {
  useDeletePatientUnavailability,
  useSavePatientUnavailibilityForPatient,
} from "../../../stores/Appointment/appointment.mutation";
import { observer } from "mobx-react";
import WeekdayAvailability from "./WeekdayAvailability";
import MobiliDate from "../../MobiliDate";
// import "./switch.css"
import MobiliTime from "../../MobiliTime";
import { formatTime } from "../../../helpers/dateFormat";
import PatientAvailabilityToggle from "./PatientAvailabilityToggle";
import {
  validateTimeInput,
  validateDateRange,
  formatDateForMobiliDate,
  getAvailabilityStatus,
  transformAvailabilityToUnavailability,
  parseTime,
  Row,
  DayData,
  checkTimeBlockOverlap,
  mergeTimeBlocks,
  generateUnavailableTimeBlocksTooltip,
  formatDateForDisplay,
} from "./helpers";

const CustomInput = styled("div")`
  display: flex;
  gap: 1px;
  align-items: center;
`;

// const SwitchWrapper = styled(Switch)`
//   && {
//     .MuiSwitch-thumb {
//       color: ${(props) =>
//         props.checked
//           ? mobiliTheme.palette.info.main
//           : mobiliTheme.palette.success.dark}; /* Your error or primary color */
//     }
//     .MuiSwitch-track {
//       background-color: ${(props) =>
//         props.checked
//           ? mobiliTheme.palette.info.main
//           : mobiliTheme.palette.success.main}; /* Your error or primary color */
//       opacity: 0.5; /* Adjust transparency as needed */
//     }
//   }
// `;

const localTableTheme = createTheme(mobiliTheme, {
  components: {
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: "#f5f5f5",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "0.7rem", // Set custom font size for this table
          padding: "0px !important", // Adjust padding if necessary
          border: "1px solid #ccc", // Add custom border color
        },
        head: {
          fontSize: "0.85rem", // Font size for header cells
          fontWeight: "bold", // Make header cells bold
          backgroundColor: "#e0e0e0", // Set background color for header
          color: "#333", // Custom text color for header cells
          borderBottom: "2px solid #999", // Custom border for header
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        body1: {
          fontSize: "0.875rem", // Customize the typography for table content
        },
      },
    },
  },
});

interface PatientUnavailabilityProps {
  patientId: number;
  patientAvailabilityData: PatientAvailabilityDto[];
  /** LLM-suggested availabilities that should appear as an unsaved draft row in edit mode */
  aiDraftAvailabilities?: PatientAvailabilityDto[];
  /** Called after the AI draft row is saved or dismissed so the parent can clear its state */
  onAiDraftClear?: () => void;
  /** Incrementing counter — each bump triggers an automatic save of the current draft */
  triggerSave?: number;
  /** Incrementing counter — each bump removes draft rows without saving */
  triggerReject?: number;
}

const PatientUnavailability: React.FC<PatientUnavailabilityProps> = observer(
  ({
    patientId,
    patientAvailabilityData,
    aiDraftAvailabilities,
    onAiDraftClear,
    triggerSave,
    triggerReject,
  }) => {
    const { mutate: savePatientUnavailabilityForPatient, isPending: isLoading } =
      useSavePatientUnavailibilityForPatient();
    const { mutate: deletePatientUnavailability } = useDeletePatientUnavailability();
    //   State to store Popover and IconButton state for each day of the week in each group
    // popovers keyed by groupId -> dayOfWeek
    const [popoverOpen, setPopoverOpen] = useState<{
      [key: string]: { [key: number]: HTMLElement | null };
    }>({});
    // Track forced disabled state for toggles per group-day
    const [toggleForceDisabled, setToggleForceDisabled] = useState<{ [key: string]: boolean }>({});
    const [activeTimeBlockUid, setActiveTimeBlockUid] = useState<string | null>(null);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    // Track if edit mode was entered from bulk apply (to show re-open bulk button)
    const [editEnteredFromBulk, setEditEnteredFromBulk] = useState(false);
    const [toggleValues, setToggleValues] = useState<{ [key: string]: string | undefined }>({});
    const [draft, setDraft] = useState<{ [key: string]: any }>({});
    const [timeValidationErrors, setTimeValidationErrors] = useState<{ [key: string]: string }>({});
    const [overlapErrors, setOverlapErrors] = useState<{ [key: string]: string }>({});
    const [dateValidityValidationErrors, setDateValidityValidationErrors] = useState<{
      [key: string]: string;
    }>({});
    const [sessionSnapshots, setSessionSnapshots] = useState<{ [key: string]: DayData[] }>({});
    const [rowSnapshots, setRowSnapshots] = useState<{ [key: string]: Row }>({});
    const [toggleValuesSnapshots, setToggleValuesSnapshots] = useState<{
      [key: string]: { [key: string]: string | undefined };
    }>({});

    // --- Bulk edit state ---
    const [bulkEditGroupId, setBulkEditGroupId] = useState<string | null>(null);
    const [bulkAnchorEl, setBulkAnchorEl] = useState<HTMLElement | null>(null);
    const [bulkSelectedDays, setBulkSelectedDays] = useState<Set<number>>(new Set());
    const [bulkAvailabilityStatus, setBulkAvailabilityStatus] = useState<"unavailable" | "partial">(
      "unavailable",
    );
    const [bulkToggleValue, setBulkToggleValue] = useState<"Verfügbar" | "Nicht verfügbar">(
      "Nicht verfügbar",
    );
    const [bulkDraft, setBulkDraft] = useState<{
      startTime: string | null;
      endTime: string | null;
    }>({ startTime: null, endTime: null });
    const [bulkTimeBlocks, setBulkTimeBlocks] = useState<
      Array<{ uid: string; startTime: string; endTime: string }>
    >([]);
    const [bulkTimeError, setBulkTimeError] = useState<string | null>(null);
    const [bulkOverlapError, setBulkOverlapError] = useState<string | null>(null);
    const [bulkConfirmDiscard, setBulkConfirmDiscard] = useState(false);
    const [bulkApplyMode, setBulkApplyMode] = useState<"replace" | "add">("replace");
    // Snapshot for when re-entering bulk edit from single-edit mode (preserves post-first-bulk state)
    const [bulkEntrySnapshot, setBulkEntrySnapshot] = useState<{
      row: Row;
      toggleValues: { [key: string]: string | undefined };
    } | null>(null);

    const transformDataToRows = (availabilityData: any) => {
      const rows = [];

      // Create a map to group data by groupId
      const groupedData = availabilityData.reduce((acc: any, data: any) => {
        const groupId = data.groupId;

        if (!acc[groupId]) {
          acc[groupId] = {
            groupId: groupId,
            validityStart:
              data.validityStart !== null ? dayjs(data.validityStart).toISOString() : null,
            validityEnd: data.validityEnd !== null ? dayjs(data.validityEnd).toISOString() : null,
            dayData: [],
            // Use createdAt from DB if available, else fallback to now (for new rows)
            createdAt: data.createdAt ? dayjs(data.createdAt).valueOf() : dayjs().valueOf(),
          };
        }

        // Simple check: if saved times are 7:00-20:00, it's "unavailable", otherwise "partial"
        const start = data.startTime ? dayjs(data.startTime) : null;
        const end = data.endTime ? dayjs(data.endTime) : null;
        const isFullDay =
          start &&
          end &&
          start.hour() === 7 &&
          start.minute() === 0 &&
          end.hour() === 20 &&
          end.minute() === 0;

        const availabilityStatus = isFullDay ? "unavailable" : "partial";

        acc[groupId].dayData.push({
          uid: data.id ? String(data.id) : uuidv4(),
          groupId: groupId,
          id: data.id,
          dayOfWeek: data.dayOfWeek,
          selected: true, // Since it's coming from availabilityData
          availabilityStatus: availabilityStatus,
          // Keep as UTC ISO string (server data is already UTC)
          startTime:
            availabilityStatus === "unavailable" ? null : data.startTime ? data.startTime : null,
          endTime: availabilityStatus === "unavailable" ? null : data.endTime ? data.endTime : null,
          createdAt: data.createdAt ? dayjs(data.createdAt).valueOf() : dayjs().valueOf(),
        });

        return acc;
      }, {});

      // Convert the map values into an array (rows)
      for (const key in groupedData) {
        // Sort dayData within each group based on dayOfWeek
        const sortedRow = {
          ...groupedData[key],
          dayData: groupedData[key].dayData.sort((a: any, b: any) => a.dayOfWeek - b.dayOfWeek),
        };
        rows.push(sortedRow);
      }

      // Sort rows by createdAt (oldest first, newest last)
      rows.sort((a, b) => {
        const aCreated =
          a.createdAt ||
          (a.dayData.length ? Math.min(...a.dayData.map((d: any) => d.createdAt || 0)) : 0);
        const bCreated =
          b.createdAt ||
          (b.dayData.length ? Math.min(...b.dayData.map((d: any) => d.createdAt || 0)) : 0);
        return aCreated - bCreated;
      });

      return rows;
    };

    /**
     * Computes "gap" rows — all-available rows for validity periods not covered by any existing row.
     * Considers rows with at least a validityEnd (fully bounded or end-only).
     * Rows without any validity dates or with only validityStart (open-ended) are excluded
     * from interval computation but open-ended rows suppress gaps they cover.
     */
    const computeGapRows = (dataRows: Row[]): Row[] => {
      // Consider rows that have a validityEnd (they have a finite end boundary)
      const withEnd = dataRows.filter((r) => r.validityEnd);
      if (withEnd.length === 0) return [];

      const intervals = withEnd.map((r) => ({
        start: r.validityStart ? dayjs(r.validityStart).startOf("day") : null,
        end: dayjs(r.validityEnd!).startOf("day"),
      }));

      // Sort by start date (null start = −∞ = first)
      intervals.sort((a, b) => {
        if (a.start === null && b.start === null) return 0;
        if (a.start === null) return -1;
        if (b.start === null) return 1;
        return a.start.valueOf() - b.start.valueOf();
      });

      // Merge overlapping / adjacent (touching by 1 day) intervals
      const merged: Array<{ start: dayjs.Dayjs | null; end: dayjs.Dayjs }> = [];
      for (const interval of intervals) {
        if (merged.length === 0) {
          merged.push({ start: interval.start, end: interval.end });
          continue;
        }
        const last = merged[merged.length - 1];
        // If last starts at null (−∞), it overlaps everything that starts before last.end + 1 day
        const overlapsOrAdjacent =
          interval.start === null ||
          last.start === null ||
          interval.start.valueOf() <= last.end.add(1, "day").valueOf();
        if (overlapsOrAdjacent) {
          if (interval.end.valueOf() > last.end.valueOf()) {
            last.end = interval.end;
          }
        } else {
          merged.push({ start: interval.start, end: interval.end });
        }
      }

      const gaps: Array<{ start: dayjs.Dayjs; end: dayjs.Dayjs | null }> = [];

      // Collect open-ended rows (validityStart set, validityEnd null) — they cover from their start to ∞
      const openEndedStarts = dataRows
        .filter((r) => r.validityStart && !r.validityEnd)
        .map((r) => dayjs(r.validityStart!).startOf("day"));

      // Helper: check if a gap is already covered by an open-ended row
      const isGapCoveredByOpenEnded = (gapStart: dayjs.Dayjs) =>
        openEndedStarts.some((oeStart) => gapStart.valueOf() >= oeStart.valueOf());

      // Gaps between consecutive merged intervals
      for (let i = 0; i < merged.length - 1; i++) {
        const gapStart = merged[i].end.add(1, "day");
        const nextStart = merged[i + 1].start;
        if (!nextStart) continue; // next interval starts at −∞, no gap possible
        const gapEnd = nextStart.subtract(1, "day");
        if (gapStart.valueOf() <= gapEnd.valueOf() && !isGapCoveredByOpenEnded(gapStart)) {
          gaps.push({ start: gapStart, end: gapEnd });
        }
      }

      // Trailing gap: after the last bounded interval ends
      // BUT only if no open-ended row already covers that period
      const trailingStart = merged[merged.length - 1].end.add(1, "day");
      if (!isGapCoveredByOpenEnded(trailingStart)) {
        gaps.push({ start: trailingStart, end: null });
      }

      return gaps.map((gap) => {
        const newGroupId = `gap-${uuidv4()}`;
        return {
          groupId: newGroupId,
          validityStart: gap.start.toISOString(),
          validityEnd: gap.end ? gap.end.toISOString() : null,
          dayData: [1, 2, 3, 4, 5].map((dow) => ({
            uid: uuidv4(),
            id: null,
            groupId: newGroupId,
            dayOfWeek: dow,
            selected: false,
            startTime: null,
            endTime: null,
          })),
        };
      });
    };

    const uuidGroupId = uuidv4();
    const [rows, setRows] = useState<Row[]>([
      {
        groupId: uuidGroupId, // Replace with actual existing groupId from your data
        validityStart: null,
        validityEnd: null,
        dayData: [
          {
            uid: uuidv4(),
            id: null,
            groupId: uuidGroupId,
            dayOfWeek: 1,
            selected: false,
            startTime: null,
            endTime: null,
          },
          {
            uid: uuidv4(),
            id: null,
            groupId: uuidGroupId,
            dayOfWeek: 2,
            selected: false,
            startTime: null,
            endTime: null,
          },
          {
            uid: uuidv4(),
            id: null,
            groupId: uuidGroupId,
            dayOfWeek: 3,
            selected: false,
            startTime: null,
            endTime: null,
          },
          {
            uid: uuidv4(),
            id: null,
            groupId: uuidGroupId,
            dayOfWeek: 4,
            selected: false,
            startTime: null,
            endTime: null,
          },
          {
            uid: uuidv4(),
            id: null,
            groupId: uuidGroupId,
            dayOfWeek: 5,
            selected: false,
            startTime: null,
            endTime: null,
          },
        ],
      },
    ]);

    // Derived: do any of the selected bulk days already have existing time block data?
    const selectedDaysHaveExistingData = useMemo(() => {
      if (!bulkEditGroupId || bulkSelectedDays.size === 0) return false;
      const currentRow = rows.find((r) => r.groupId === bulkEditGroupId);
      if (!currentRow) return false;
      return Array.from(bulkSelectedDays).some((dayOfWeek) =>
        currentRow.dayData.some(
          (d) => d.dayOfWeek === dayOfWeek && d.selected && d.startTime !== null,
        ),
      );
    }, [bulkEditGroupId, bulkSelectedDays, rows]);

    // When switching to "add" mode with existing data, lock toggle to "Nicht verfügbar"
    // (existing saved data always uses "Nicht verfügbar")
    useEffect(() => {
      if (bulkApplyMode === "add" && selectedDaysHaveExistingData) {
        setBulkToggleValue("Nicht verfügbar");
      }
    }, [bulkApplyMode, selectedDaysHaveExistingData]);

    useEffect(() => {
      const transformedRows = transformDataToRows(patientAvailabilityData);
      if (transformedRows.length !== 0) {
        // Compute gap rows (all-available rows for validity periods not covered by any existing row)
        const gapRows = computeGapRows(transformedRows);
        // Append gap rows after real rows (no re-sorting — order preserved from API/createdAt)
        const allRows = [...transformedRows, ...gapRows];
        setRows((prevRows) => {
          // Preserve AI draft row if it exists
          const draftRow = aiDraftGroupId
            ? prevRows.find((r) => r.groupId === aiDraftGroupId)
            : null;
          return draftRow ? [...allRows, draftRow] : allRows;
        });
        // Set toggle values for existing (real) entries; gap rows have no selections
        const newToggleValues: { [key: string]: string } = {};
        transformedRows.forEach((row) => {
          row.dayData.forEach((day: DayData) => {
            if (day.selected) {
              newToggleValues[`${row.groupId}-${day.dayOfWeek}`] = "Nicht verfügbar";
            }
          });
        });
        setToggleValues(newToggleValues);
      } else {
        // Add a default row when there's no data from the API
        const newGroupId = uuidv4();
        const defaultRow = {
          groupId: newGroupId,
          validityStart: null,
          validityEnd: null,
          dayData: [
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 1,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 2,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 3,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 4,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 5,
              selected: false,
              startTime: null,
              endTime: null,
            },
          ],
        };
        setRows((prevRows) => {
          const draftRow = aiDraftGroupId
            ? prevRows.find((r) => r.groupId === aiDraftGroupId)
            : null;
          return draftRow ? [defaultRow, draftRow] : [defaultRow];
        });
        setToggleValues({}); // Reset to empty for new rows
      }
    }, [patientAvailabilityData]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- AI draft injection: when aiDraftAvailabilities arrives, add a new unsaved row and auto-enter edit mode ---
    const [aiDraftGroupId, _setAiDraftGroupId] = useState<string | null>(null);
    const aiDraftGroupIdRef = useRef<string | null>(null);
    const setAiDraftGroupId = (val: string | null) => {
      aiDraftGroupIdRef.current = val;
      _setAiDraftGroupId(val);
    };

    useEffect(() => {
      if (!aiDraftAvailabilities || aiDraftAvailabilities.length === 0) {
        return;
      }

      // Build a lookup of AI draft data keyed by dayOfWeek (multiple blocks per day)
      const draftByDay = new Map<number, PatientAvailabilityDto[]>();
      aiDraftAvailabilities.forEach((avail) => {
        if (avail.dayOfWeek != null) {
          const existing = draftByDay.get(avail.dayOfWeek) || [];
          existing.push(avail);
          draftByDay.set(avail.dayOfWeek, existing);
        }
      });

      // Create row exactly like handleAddRow — all 5 weekdays, pre-filled where AI data exists
      const newGroupId = uuidv4();
      const dayData: DayData[] = [1, 2, 3, 4, 5].flatMap((dow) => {
        const avails = draftByDay.get(dow);
        if (avails && avails.length > 0) {
          return avails.map((avail) => {
            const start = avail.startTime ? dayjs(avail.startTime) : null;
            const end = avail.endTime ? dayjs(avail.endTime) : null;
            const isFullDay =
              start &&
              end &&
              start.hour() === 7 &&
              start.minute() === 0 &&
              end.hour() === 20 &&
              end.minute() === 0;
            return {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: dow,
              selected: true,
              availabilityStatus: isFullDay ? "unavailable" : "partial",
              startTime: isFullDay ? null : avail.startTime,
              endTime: isFullDay ? null : avail.endTime,
            } as DayData;
          });
        }
        return [
          {
            uid: uuidv4(),
            id: null,
            groupId: newGroupId,
            dayOfWeek: dow,
            selected: false,
            startTime: null,
            endTime: null,
          } as DayData,
        ];
      });

      setAiDraftGroupId(newGroupId);
      setRows((prevRows) => [
        ...prevRows,
        {
          groupId: newGroupId,
          validityStart: null,
          validityEnd: null,
          dayData,
        },
      ]);
    }, [aiDraftAvailabilities]); // eslint-disable-line react-hooks/exhaustive-deps

    const daysOfWeek = ["Mo", "Di", "Mi", "Do", "Fr"];

    const handlePopoverOpen = (groupId: string, dayOfWeek: number, anchor: HTMLElement) => {
      // Create a snapshot of the current day's state when popup opens
      const dayKey = `${groupId}-${dayOfWeek}`;
      const currentRow = rows.find((r) => r.groupId === groupId);
      if (currentRow) {
        const dayBlocks = currentRow.dayData.filter((d) => d.dayOfWeek === dayOfWeek);
        setSessionSnapshots((prev) => ({
          ...prev,
          [dayKey]: [...dayBlocks], // copy all day blocks at session start
        }));
      }

      setPopoverOpen((prev) => ({
        ...prev,
        [groupId]: {
          ...prev[groupId],
          [dayOfWeek]: anchor,
        },
      }));
    };

    const handlePopoverClose = (groupId: string, dayOfWeek: number) => {
      setPopoverOpen((prev) => ({
        ...prev,
        [groupId]: {
          ...prev[groupId],
          [dayOfWeek]: null,
        },
      }));
    };

    const handleDayToggle = (groupId: string, dayOfWeek: number) => {
      // setRows((prevRows) =>
      //   prevRows.map((row) =>
      //     row.groupId === groupId
      //       ? {
      //           ...row,
      //           dayData: row.dayData.some((day) => day.dayOfWeek === dayOfWeek)
      //             ? row.dayData.map((day) =>
      //                 day.dayOfWeek === dayOfWeek
      //                   ? {
      //                       ...day,
      //                       selected: !day.selected,
      //                       startTime: day.selected ? day.startTime : null,
      //                       endTime: day.selected ? day.endTime : null,
      //                     }
      //                   : day
      //               )
      //             : [
      //                 ...row.dayData,
      //                 {
      //                   id: null,
      //                   groupId: groupId,
      //                   dayOfWeek: dayOfWeek,
      //                   selected: true,
      //                   startTime: null,
      //                   endTime: null,
      //                 },
      //               ],
      //         }
      //       : row
      //   )
      // );
      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.groupId !== groupId) return row;
          const blocks = row.dayData.filter((d: any) => d.dayOfWeek === dayOfWeek);
          if (blocks.length) {
            // toggle all blocks for that day (keeps times when toggling off)
            return {
              ...row,
              dayData: row.dayData.map((day: any) =>
                day.dayOfWeek === dayOfWeek
                  ? {
                      ...day,
                      selected: !day.selected,
                      startTime: day.selected ? day.startTime : null,
                      endTime: day.selected ? day.endTime : null,
                    }
                  : day,
              ),
            };
          } else {
            // add first block for the day
            const newDay: DayData = {
              uid: uuidv4(),
              id: null,
              groupId: groupId,
              dayOfWeek: dayOfWeek,
              selected: true,
              startTime: null,
              endTime: null,
            };
            return {
              ...row,
              dayData: [...row.dayData, newDay],
            };
          }
        }),
      );
    };

    const handleTimeChange = (
      value: string,
      groupId: string,
      dayOfWeek: number,
      timeType: "startTime" | "endTime",
    ) => {
      const dayKey = `${groupId}-${dayOfWeek}`;
      const currentDraft = draft[dayKey] || {};
      const newDraft = {
        ...currentDraft,
        [timeType]: value,
      };

      setDraft((p) => ({
        ...p,
        [dayKey]: newDraft,
      }));

      // Validate time input
      const error = validateTimeInput(newDraft.startTime, newDraft.endTime);
      setTimeValidationErrors((prev) => {
        if (error) {
          return { ...prev, [dayKey]: error };
        } else {
          const { [dayKey]: _, ...rest } = prev;
          return rest;
        }
      });

      // Only check for overlaps if the time input is valid
      if (!error && newDraft.startTime && newDraft.endTime) {
        const currentRow = rows.find((r) => r.groupId === groupId);
        const existingBlocks =
          currentRow?.dayData.filter(
            (block) => block.dayOfWeek === dayOfWeek && block.startTime && block.endTime,
          ) || [];
        const overlapResult = checkTimeBlockOverlap(
          newDraft.startTime,
          newDraft.endTime,
          existingBlocks,
        );
        const overlapError = overlapResult.hasOverlap ? overlapResult.message || null : null;
        setOverlapErrors((prev) => {
          if (overlapError) {
            return { ...prev, [dayKey]: overlapError };
          } else {
            const { [dayKey]: _, ...rest } = prev;
            return rest;
          }
        });
      } else {
        // Clear overlap errors when time input is invalid or incomplete
        setOverlapErrors((prev) => {
          const { [dayKey]: _, ...rest } = prev;
          return rest;
        });
      }
    };

    const handleTimeBlur = (
      groupId: string,
      dayOfWeek: number,
      timeType: "startTime" | "endTime",
    ) => {
      const dayKey = `${groupId}-${dayOfWeek}`;
      const currentDraft = draft[dayKey] || {};
      const currentValue = currentDraft[timeType];

      // If value exists and doesn't have minutes, auto-complete with :00
      if (currentValue && /^\d{1,2}$/.test(currentValue)) {
        const hours = parseInt(currentValue, 10);
        if (hours >= 0 && hours <= 23) {
          const completedValue = `${hours.toString().padStart(2, "0")}:00`;
          handleTimeChange(completedValue, groupId, dayOfWeek, timeType);
        }
      }
    };

    const addTimeBlock = (
      groupId: string,
      dayOfWeek: number,
      startTime: string | Date | null = null,
      endTime: string | Date | null = null,
    ) => {
      const uid = uuidv4();

      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.groupId !== groupId) return row;

          // Check if there's an existing blank entry for this day
          const blankEntryIndex = row.dayData.findIndex(
            (d) => d.dayOfWeek === dayOfWeek && !d.startTime && !d.endTime && d.selected,
          );

          if (blankEntryIndex !== -1) {
            // Update the blank entry with times
            const updatedDayData = [...row.dayData];
            updatedDayData[blankEntryIndex] = {
              ...updatedDayData[blankEntryIndex],
              availabilityStatus: "partial",
              startTime: startTime
                ? typeof startTime === "string"
                  ? (parseTime(startTime, true) as string)
                  : new Date(startTime).toISOString()
                : null,
              endTime: endTime
                ? typeof endTime === "string"
                  ? (parseTime(endTime, true) as string)
                  : new Date(endTime).toISOString()
                : null,
            };
            return { ...row, dayData: updatedDayData };
          } else {
            // Add a new time block
            const newDay: DayData = {
              uid,
              id: null,
              groupId,
              dayOfWeek,
              selected: true,
              availabilityStatus: "partial",
              startTime: startTime
                ? typeof startTime === "string"
                  ? (parseTime(startTime, true) as string)
                  : new Date(startTime).toISOString()
                : null,
              endTime: endTime
                ? typeof endTime === "string"
                  ? (parseTime(endTime, true) as string)
                  : new Date(endTime).toISOString()
                : null,
            };
            return { ...row, dayData: [...row.dayData, newDay] };
          }
        }),
      );

      setActiveTimeBlockUid(uid);
      return uid;
    };

    const deleteTimeBlock = (groupId: string, uid: string) => {
      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.groupId !== groupId) return row;
          const blockToDelete = row.dayData.find((d) => d.uid === uid);
          if (!blockToDelete) return row;
          const dayOfWeek = blockToDelete.dayOfWeek;
          const filteredDayData = row.dayData.filter((d: any) => d.uid !== uid);
          const remainingBlocksForDay = filteredDayData.filter((d) => d.dayOfWeek === dayOfWeek);
          if (remainingBlocksForDay.length === 0) {
            if (blockToDelete.startTime) {
              // was partial
              // add partial block with no times
              const newBlock: DayData = {
                uid: uuidv4(),
                id: null,
                groupId,
                dayOfWeek,
                selected: true,
                availabilityStatus: "partial",
                startTime: null,
                endTime: null,
              };
              return { ...row, dayData: [...filteredDayData, newBlock] };
            } else {
              // was unavailable, remove it
              return { ...row, dayData: filteredDayData };
            }
          } else {
            return { ...row, dayData: filteredDayData };
          }
        }),
      );
    };

    const handleValidityChange = (groupId: string, field: string, value: string | null) => {
      // value is already an ISO string from MobiliDate, convert to dayjs for storage
      const parsedValue = value ? dayjs(value).toISOString() : null;

      setRows((prevRows) => {
        const updatedRows = prevRows.map((row) =>
          row.groupId === groupId ? { ...row, [field]: parsedValue } : row,
        );

        // Validate date range only when both dates are complete
        const updatedRow = updatedRows.find((r) => r.groupId === groupId);
        if (updatedRow && updatedRow.validityStart && updatedRow.validityEnd) {
          const error = validateDateRange(updatedRow.validityStart, updatedRow.validityEnd);
          setDateValidityValidationErrors((prev) => {
            if (error) {
              return { ...prev, [groupId]: error };
            } else {
              const { [groupId]: _, ...rest } = prev;
              return rest;
            }
          });
        } else {
          // Clear error if either date is empty
          setDateValidityValidationErrors((prev) => {
            const { [groupId]: _, ...rest } = prev;
            return rest;
          });
        }

        return updatedRows;
      });
    };

    const handleAddRow = () => {
      const newGroupId = uuidv4();

      setRows((prevRows) => [
        ...prevRows,
        {
          groupId: newGroupId,
          validityStart: null,
          validityEnd: null,
          dayData: [
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 1,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 2,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 3,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 4,
              selected: false,
              startTime: null,
              endTime: null,
            },
            {
              uid: uuidv4(),
              id: null,
              groupId: newGroupId,
              dayOfWeek: 5,
              selected: false,
              startTime: null,
              endTime: null,
            },
          ],
        },
      ]);
    };

    const handleSaveRow = async () => {
      if (!editingGroupId) return;
      const isAiDraft = editingGroupId === aiDraftGroupId;

      try {
        // setLoading(true);
        const transformedData = transformInputData();
        // console.log("transformedData", transformedData);
        if (transformedData.length > 0) {
          savePatientUnavailabilityForPatient(
            { patientId, unavailabilityData: transformedData },
            {
              onSuccess: (response) => {
                // console.log("Save successful, response:", response);
                // Revert any "partial" days with no times back to "available"
                setRows((prevRows) =>
                  prevRows.map((row) => {
                    if (row.groupId !== editingGroupId) return row;
                    return {
                      ...row,
                      dayData: row.dayData.map((day) => {
                        if (
                          day.availabilityStatus === "partial" &&
                          day.selected &&
                          !day.startTime &&
                          !day.endTime
                        ) {
                          return { ...day, selected: false, availabilityStatus: undefined };
                        }
                        return day;
                      }),
                    };
                  }),
                );
                // Update snapshot to current state after successful save
                const currentRow = rows.find((r) => r.groupId === editingGroupId);
                if (currentRow) {
                  setRowSnapshots((prev) => ({
                    ...prev,
                    [editingGroupId]: JSON.parse(JSON.stringify(currentRow)),
                  }));
                }
                setEditingGroupId(null);
                setEditEnteredFromBulk(false);
                // Clear AI draft state after successful save
                if (isAiDraft) {
                  setAiDraftGroupId(null);
                  onAiDraftClear?.();
                }
              },
              onError: (error) => {
                console.error("Save failed:", error);
              },
            },
          );
        } else {
          // Revert any "partial" days with no times back to "available"
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.groupId !== editingGroupId) return row;
              return {
                ...row,
                dayData: row.dayData.map((day) => {
                  if (
                    day.availabilityStatus === "partial" &&
                    day.selected &&
                    !day.startTime &&
                    !day.endTime
                  ) {
                    return { ...day, selected: false, availabilityStatus: undefined };
                  }
                  return day;
                }),
              };
            }),
          );
          setEditingGroupId(null);
          setEditEnteredFromBulk(false);
          if (isAiDraft) {
            setAiDraftGroupId(null);
            onAiDraftClear?.();
          }
        }
      } catch (error) {
        console.error("Error saving data:", error);
        // } finally {
        //   setLoading(false);
      }
    };

    // Auto-save when parent triggers approval
    useEffect(() => {
      const draftGroupId = aiDraftGroupIdRef.current;
      if (triggerSave && triggerSave > 0 && draftGroupId) {
        const draftRow = rows.find((r) => r.groupId === draftGroupId);
        if (!draftRow) return;

        const transformedData: PatientAvailabilityDto[] = [];
        draftRow.dayData.forEach((day: any) => {
          if (!day.selected || day.availabilityStatus === "available") return;

          let startTime: string | null = null;
          let endTime: string | null = null;

          if (day.availabilityStatus === "partial") {
            startTime = day.startTime;
            endTime = day.endTime;
            if (!startTime || !endTime) return;
          } else if (day.availabilityStatus === "unavailable") {
            startTime = dayjs().hour(7).minute(0).second(0).millisecond(0).toISOString();
            endTime = dayjs().hour(20).minute(0).second(0).millisecond(0).toISOString();
          }

          transformedData.push({
            groupId: draftRow.groupId,
            dayOfWeek: day.dayOfWeek,
            startTime: startTime ?? undefined,
            endTime: endTime ?? undefined,
            validityStart: draftRow.validityStart
              ? dayjs(draftRow.validityStart).toISOString()
              : undefined,
            validityEnd: draftRow.validityEnd
              ? dayjs(draftRow.validityEnd).toISOString()
              : undefined,
          });
        });

        // Clear only the REF immediately — this prevents the query-invalidation
        // race (refetched patientAvailabilityData re-triggering draft injection).
        // Keep UI state (rows, aiDraftGroupId state) so we can restore on error.
        aiDraftGroupIdRef.current = null;

        if (transformedData.length > 0) {
          savePatientUnavailabilityForPatient(
            { patientId, unavailabilityData: transformedData },
            {
              onSuccess: () => {
                setAiDraftGroupId(null);
                setEditingGroupId(null);
                setRows((prev) => prev.filter((r) => r.groupId !== draftGroupId));
                onAiDraftClear?.();
              },
              onError: (error) => {
                console.error("AI draft save failed:", error);
                // Restore the ref so a retry via triggerSave or manual save works
                aiDraftGroupIdRef.current = draftGroupId;
              },
            },
          );
        } else {
          setAiDraftGroupId(null);
          setEditingGroupId(null);
          setRows((prev) => prev.filter((r) => r.groupId !== draftGroupId));
          onAiDraftClear?.();
        }
      }
    }, [triggerSave]); // eslint-disable-line react-hooks/exhaustive-deps

    // Remove draft row when parent triggers rejection
    useEffect(() => {
      const draftGroupId = aiDraftGroupIdRef.current;
      if (triggerReject && triggerReject > 0 && draftGroupId) {
        setRows((prev) => prev.filter((r) => r.groupId !== draftGroupId));
        setEditingGroupId(null);
        setAiDraftGroupId(null);
        onAiDraftClear?.();
      }
    }, [triggerReject]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDeleteRow = async (groupId: string) => {
      try {
        const rowToDelete = rows.find((row) => row.groupId === groupId);

        if (rowToDelete && patientAvailabilityData.some((data) => data.groupId === groupId)) {
          // Saved row — delete via API
          try {
            deletePatientUnavailability({ groupId: rowToDelete.groupId, patientId });
            setRows((prevRows) => prevRows.filter((row) => row.groupId !== groupId));
          } catch (error) {
            console.error("Error deleting row:", error);
          }
        } else {
          // Unsaved row (manual, AI draft, or gap row) — remove from state
          setRows((prevRows) => prevRows.filter((row) => row.groupId !== groupId));

          // After a brief delay, recompute gap rows so they reappear if the data still implies a gap
          setTimeout(() => {
            const transformedRows = transformDataToRows(patientAvailabilityData);
            if (transformedRows.length !== 0) {
              const gapRows = computeGapRows(transformedRows);
              const allRows = [...transformedRows, ...gapRows];
              setRows((prevRows) => {
                // Preserve manually added or AI draft rows, but NOT old gap row instances
                const unsavedNonGapRows = prevRows.filter(
                  (r) =>
                    !transformedRows.some((tr) => tr.groupId === r.groupId) &&
                    !r.groupId.startsWith("gap-"),
                );
                return [...allRows, ...unsavedNonGapRows];
              });
            }
          }, 300);
        }

        // Clean up AI draft state if this was the draft row
        if (groupId === aiDraftGroupId) {
          setAiDraftGroupId(null);
          setEditingGroupId(null);
          onAiDraftClear?.();
        }
      } catch (error) {
        console.error("Error deleting row:", error);
      }
    };

    const handleEditRow = async (groupId: string) => {
      // Save snapshot of the row when entering edit mode
      const currentRow = rows.find((r) => r.groupId === groupId);
      if (currentRow) {
        setRowSnapshots((prev) => ({
          ...prev,
          [groupId]: JSON.parse(JSON.stringify(currentRow)),
        }));
        // Snapshot toggle values for this group's days
        const tvSnap: { [key: string]: string | undefined } = {};
        for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
          const key = `${groupId}-${dayOfWeek}`;
          tvSnap[key] = toggleValues[key];
        }
        setToggleValuesSnapshots((prev) => ({ ...prev, [groupId]: tvSnap }));
      }
      setEditingGroupId(groupId);
      setEditEnteredFromBulk(false);
    };

    const hasRowChanges = (groupId: string): boolean => {
      // AI draft rows always have changes (they need to be saved)
      if (groupId === aiDraftGroupId) {
        const currentRow = rows.find((r) => r.groupId === groupId);
        return currentRow ? currentRow.dayData.some((day) => day.selected) : false;
      }

      const snapshot = rowSnapshots[groupId];
      if (!snapshot) return false;

      const currentRow = rows.find((r) => r.groupId === groupId);
      if (!currentRow) return false;

      // Check if at least one day is selected/has availability status set to partial or unavailable
      const hasAnySelectedDay = currentRow.dayData.some((day) => day.selected);

      // If no days are selected (all available), disable save regardless of validity changes
      if (!hasAnySelectedDay) {
        return false;
      }

      // Check if at least one availability status is changed
      let hasAvailabilityChanges = false;

      // Check if dayData length changed
      if (currentRow.dayData.length !== snapshot.dayData.length) {
        hasAvailabilityChanges = true;
      }

      if (!hasAvailabilityChanges) {
        // Compare each day's data for availability changes
        for (let i = 0; i < currentRow.dayData.length; i++) {
          const currentDay = currentRow.dayData[i];
          const snapshotDay = snapshot.dayData.find(
            (d) => d.dayOfWeek === currentDay.dayOfWeek && d.uid === currentDay.uid,
          );

          if (!snapshotDay) {
            hasAvailabilityChanges = true;
            break;
          }

          // Check availability-related fields
          if (
            currentDay.selected !== snapshotDay.selected ||
            currentDay.availabilityStatus !== snapshotDay.availabilityStatus ||
            currentDay.startTime !== snapshotDay.startTime ||
            currentDay.endTime !== snapshotDay.endTime
          ) {
            hasAvailabilityChanges = true;
            break;
          }
        }
      }

      if (!hasAvailabilityChanges) {
        // Check if any day blocks were removed
        for (const snapshotDay of snapshot.dayData) {
          const currentDay = currentRow.dayData.find(
            (d) => d.dayOfWeek === snapshotDay.dayOfWeek && d.uid === snapshotDay.uid,
          );
          if (!currentDay) {
            hasAvailabilityChanges = true;
            break;
          }
        }
      }

      // Check validity dates
      const hasValidityChanges =
        currentRow.validityStart !== snapshot.validityStart ||
        currentRow.validityEnd !== snapshot.validityEnd;

      // Enable save if there are availability changes OR validity changes
      return hasAvailabilityChanges || hasValidityChanges;
    };

    const handleAvailabilityChange = (
      groupId: string,
      dayOfWeek: number,
      newStatus: "available" | "unavailable" | "partial",
      anchorEl?: HTMLElement | null,
    ) => {
      // Check if changing to partial from a different status
      const currentRow = rows.find((r) => r.groupId === groupId);
      if (currentRow) {
        const currentDay = currentRow.dayData.find((d) => d.dayOfWeek === dayOfWeek);
        const currentAvailability = currentDay ? getAvailabilityStatus(currentDay) : "available";
        if (currentAvailability !== "partial" && newStatus === "partial") {
          // Reset toggle to default when switching to partial from any other status
          setToggleValues((prev) => ({ ...prev, [`${groupId}-${dayOfWeek}`]: "Nicht verfügbar" }));

          // Clear any draft inputs when switching to partial
          const dayKey = `${groupId}-${dayOfWeek}`;
          setDraft((prev) => {
            const newDraft = { ...prev };
            delete newDraft[dayKey];
            return newDraft;
          });
        }
      }

      setRows((prevRows) =>
        prevRows.map((row) =>
          row.groupId === groupId
            ? {
                ...row,
                dayData: (() => {
                  if (newStatus === "unavailable") {
                    // For unavailable, consolidate to a single entry per day
                    const filteredDayData = row.dayData.filter(
                      (day) => day.dayOfWeek !== dayOfWeek,
                    );
                    const newDay: DayData = {
                      uid: uuidv4(),
                      id: null,
                      groupId: groupId,
                      dayOfWeek: dayOfWeek,
                      selected: true,
                      availabilityStatus: "unavailable",
                      startTime: null,
                      endTime: null,
                    };
                    return [...filteredDayData, newDay];
                  } else if (newStatus === "available") {
                    // For available, remove all entries for that day
                    return row.dayData.filter((day) => day.dayOfWeek !== dayOfWeek);
                  } else if (newStatus === "partial") {
                    // For partial, check if we already have blocks for this day
                    const existingDayBlocks = row.dayData.filter(
                      (day) => day.dayOfWeek === dayOfWeek,
                    );
                    const filteredDayData = row.dayData.filter(
                      (day) => day.dayOfWeek !== dayOfWeek,
                    );

                    // If we have existing blocks with data, preserve them but update status
                    if (
                      existingDayBlocks.length > 0 &&
                      existingDayBlocks.some((block) => block.startTime && block.endTime)
                    ) {
                      const updatedBlocks = existingDayBlocks.map((block) => ({
                        ...block,
                        selected: true,
                        availabilityStatus: "partial" as const,
                      }));
                      return [...filteredDayData, ...updatedBlocks];
                    } else {
                      // Create fresh partial block only if no existing data
                      const newPartialDay: DayData = {
                        uid: uuidv4(),
                        id: null,
                        groupId: groupId,
                        dayOfWeek: dayOfWeek,
                        selected: true,
                        availabilityStatus: "partial",
                        startTime: null,
                        endTime: null,
                      };
                      return [...filteredDayData, newPartialDay];
                    }
                  } else {
                    // Fallback for other cases
                    return row.dayData.some((day) => day.dayOfWeek === dayOfWeek)
                      ? row.dayData.map((day) =>
                          day.dayOfWeek === dayOfWeek
                            ? {
                                ...day,
                                selected: true,
                                availabilityStatus: newStatus,
                                startTime: day.startTime,
                                endTime: day.endTime,
                              }
                            : day,
                        )
                      : [
                          ...row.dayData,
                          {
                            uid: uuidv4(),
                            id: null,
                            groupId: groupId,
                            dayOfWeek: dayOfWeek,
                            selected: true,
                            availabilityStatus: newStatus,
                            startTime: null,
                            endTime: null,
                          },
                        ];
                  }
                })(),
              }
            : row,
        ),
      );

      if (newStatus === "partial" && anchorEl) {
        handlePopoverOpen(groupId, dayOfWeek, anchorEl);
      }
    };

    // clicking cancel inside popup
    const handleCancelPopup = (groupId: string, dayOfWeek: number) => {
      // Close popup immediately to prevent visual flashing
      handlePopoverClose(groupId, dayOfWeek);

      // Defer all state updates to the next frame to prevent visual flash
      setTimeout(() => {
        // Clear draft state for this day (temporary inputs)
        const dayKey = `${groupId}-${dayOfWeek}`;

        // Restore from session snapshot (undoes ALL changes made during this session)
        const sessionSnapshot = sessionSnapshots[dayKey];
        if (sessionSnapshot) {
          setRows((prevRows) =>
            prevRows.map((row) => {
              if (row.groupId !== groupId) return row;

              // Remove all blocks for this day and restore from snapshot
              const otherDayBlocks = row.dayData.filter((d) => d.dayOfWeek !== dayOfWeek);
              return {
                ...row,
                dayData: [...otherDayBlocks, ...sessionSnapshot],
              };
            }),
          );

          // Clear the snapshot after restoring
          setSessionSnapshots((prev) => {
            const { [dayKey]: _, ...newSnapshots } = prev;
            return newSnapshots;
          });
        }

        // Clear draft state for this day (temporary inputs)
        setDraft((prev) => {
          const { [dayKey]: _, ...newDraft } = prev;
          return newDraft;
        });

        // Clear validation errors for this day
        setTimeValidationErrors((prev) => {
          const { [dayKey]: _, ...newErrors } = prev;
          return newErrors;
        });

        // Clear overlap errors for this day
        setOverlapErrors((prev) => {
          const { [dayKey]: _, ...newErrors } = prev;
          return newErrors;
        });

        // Clear active edit UID if it was for this day
        setActiveTimeBlockUid((prev) => {
          if (prev) {
            // Find if the active edit UID belongs to this day
            const currentRow = rows.find((row) => row.groupId === groupId);
            const activeBlock = currentRow?.dayData.find((day) => day.uid === prev);
            if (activeBlock && activeBlock.dayOfWeek === dayOfWeek) {
              return null;
            }
          }
          return prev;
        });
      }, 0);
    };

    const handleCancelRow = (groupId: string) => {
      // If it's an AI draft, remove the row entirely and notify parent
      if (groupId === aiDraftGroupId) {
        setRows((prev) => prev.filter((r) => r.groupId !== groupId));
        setEditingGroupId(null);
        setAiDraftGroupId(null);
        onAiDraftClear?.();
        return;
      }

      // Restore the entire row from snapshot if it exists
      const rowSnapshot = rowSnapshots[groupId];
      if (rowSnapshot) {
        // Deep clone the snapshot to ensure complete restoration
        const restoredRow = JSON.parse(JSON.stringify(rowSnapshot));
        setRows((prevRows) => prevRows.map((row) => (row.groupId === groupId ? restoredRow : row)));

        // Clear the snapshot after restoring
        setRowSnapshots((prev) => {
          const { [groupId]: _, ...rest } = prev;
          return rest;
        });
      }

      // Clear all session snapshots for this group
      setSessionSnapshots((prev) => {
        const newSnapshots = { ...prev };
        for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
          const dayKey = `${groupId}-${dayOfWeek}`;
          delete newSnapshots[dayKey];
        }
        return newSnapshots;
      });

      // Close all popovers for this group
      setPopoverOpen((prev) => ({
        ...prev,
        [groupId]: {},
      }));

      // Clear draft state for all days in this group
      setDraft((prev) => {
        const newDraft = { ...prev };
        for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
          const dayKey = `${groupId}-${dayOfWeek}`;
          delete newDraft[dayKey];
        }
        return newDraft;
      });

      // Clear validation errors for all days in this group
      setTimeValidationErrors((prev) => {
        const newErrors = { ...prev };
        for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
          const dayKey = `${groupId}-${dayOfWeek}`;
          delete newErrors[dayKey];
        }
        return newErrors;
      });

      // Clear overlap errors for all days in this group
      setOverlapErrors((prev) => {
        const newErrors = { ...prev };
        for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
          const dayKey = `${groupId}-${dayOfWeek}`;
          delete newErrors[dayKey];
        }
        return newErrors;
      });

      // Clear toggle forced disabled state for this group
      setToggleForceDisabled((prev) => {
        const newState = { ...prev };
        for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
          const dayKey = `${groupId}-${dayOfWeek}`;
          delete newState[dayKey];
        }
        return newState;
      });

      // Restore toggle values from snapshot
      const tvSnap = toggleValuesSnapshots[groupId];
      if (tvSnap) {
        setToggleValues((prev) => {
          const updated = { ...prev };
          for (const key of Object.keys(tvSnap)) {
            if (tvSnap[key] !== undefined) {
              updated[key] = tvSnap[key];
            } else {
              delete updated[key];
            }
          }
          return updated;
        });
        setToggleValuesSnapshots((prev) => {
          const { [groupId]: _, ...rest } = prev;
          return rest;
        });
      }

      // Clear validity validation errors for this group
      setDateValidityValidationErrors((prev) => {
        const { [groupId]: _, ...rest } = prev;
        return rest;
      });

      // Clear active time block UID if it belongs to this group
      setActiveTimeBlockUid(null);

      setEditingGroupId(null);
      setEditEnteredFromBulk(false);
    };

    // clicking ok/done inside popup
    const handleOkPopup = (groupId: string, dayOfWeek: number) => {
      const dayKey = `${groupId}-${dayOfWeek}`;

      // Clear any session snapshot since changes are accepted
      setSessionSnapshots((prev) => {
        const { [dayKey]: _, ...newSnapshots } = prev;
        return newSnapshots;
      });

      // Clear draft state for this day
      setDraft((prev) => {
        const { [dayKey]: _, ...newDraft } = prev;
        return newDraft;
      });

      // Clear validation errors for this day
      setTimeValidationErrors((prev) => {
        const { [dayKey]: _, ...newErrors } = prev;
        return newErrors;
      });

      // Clear overlap errors for this day
      setOverlapErrors((prev) => {
        const { [dayKey]: _, ...newErrors } = prev;
        return newErrors;
      });

      handlePopoverClose(groupId, dayOfWeek);
    };

    // clearing all time blocks
    const handleClearAll = (groupId: string, dayOfWeek: number) => {
      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.groupId !== groupId) return row;

          // Remove all day blocks for this specific day
          const filteredDayData = row.dayData.filter((d) => d.dayOfWeek !== dayOfWeek);

          // Add back a single empty partial block for this day
          const newEmptyBlock: DayData = {
            uid: uuidv4(),
            id: null,
            groupId,
            dayOfWeek,
            selected: true,
            availabilityStatus: "partial",
            startTime: null,
            endTime: null,
          };

          return { ...row, dayData: [...filteredDayData, newEmptyBlock] };
        }),
      );

      // Clear the draft inputs for this day as well
      const dayKey = `${groupId}-${dayOfWeek}`;
      setDraft((prev) => {
        const { [dayKey]: _, ...newDraft } = prev;
        return newDraft;
      });

      // Clear validation errors for this day
      setTimeValidationErrors((prev) => {
        const { [dayKey]: _, ...newErrors } = prev;
        return newErrors;
      });

      // Clear overlap errors for this day
      setOverlapErrors((prev) => {
        const { [dayKey]: _, ...newErrors } = prev;
        return newErrors;
      });

      // Force disable the toggle for this group-day
      setToggleForceDisabled((prev) => ({ ...prev, [dayKey]: true }));
    };

    // click reset restarts the entry mode, and deletes all time blocks entered & saved if any
    const handleReset = (groupId: string, dayOfWeek: number) => {
      // Clear all time blocks for this day but keep it in partial state
      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.groupId !== groupId) return row;

          // Remove all blocks for this specific day only
          const filteredDayData = row.dayData.filter((d) => d.dayOfWeek !== dayOfWeek);

          // Add a fresh partial block for this day (selected=true, partial state, no times)
          const newPartialBlock = {
            uid: uuidv4(),
            id: null,
            groupId: groupId,
            dayOfWeek: dayOfWeek,
            selected: true,
            availabilityStatus: "partial" as const,
            startTime: null,
            endTime: null,
          };

          return {
            ...row,
            dayData: [...filteredDayData, newPartialBlock],
          };
        }),
      );

      // Reset toggle to default value for partial state
      const dayKey = `${groupId}-${dayOfWeek}`;
      setToggleValues((prev) => ({
        ...prev,
        [dayKey]: "Nicht verfügbar", // Default toggle value for partial state
      }));

      // Reset forced disabled state for this group-day
      setToggleForceDisabled((prev) => {
        const { [dayKey]: _, ...rest } = prev;
        return rest;
      });

      // Clear draft inputs for this day
      setDraft((prev) => {
        const newDraft = { ...prev };
        delete newDraft[dayKey];
        return newDraft;
      });

      // Clear validation errors for this day
      setTimeValidationErrors((prev) => {
        const { [dayKey]: _, ...newErrors } = prev;
        return newErrors;
      });

      // Clear overlap errors for this day
      setOverlapErrors((prev) => {
        const { [dayKey]: _, ...newErrors } = prev;
        return newErrors;
      });

      // Clear any active edit states for this day
      setActiveTimeBlockUid((prev) => {
        if (prev) {
          // Find if the active edit UID belongs to this day
          const currentRow = rows.find((row) => row.groupId === groupId);
          const activeBlock = currentRow?.dayData.find((day) => day.uid === prev);
          if (activeBlock && activeBlock.dayOfWeek === dayOfWeek) {
            return null;
          }
        }
        return prev;
      });
    };

    // --- Bulk edit handlers ---
    const handleBulkEditRow = (groupId: string, anchorEl: HTMLElement) => {
      const currentRow = rows.find((r) => r.groupId === groupId);

      if (editingGroupId === groupId) {
        // Re-entering bulk from edit mode: save current state for bulk-cancel-back-to-edit
        if (currentRow) {
          const tvSnap: { [key: string]: string | undefined } = {};
          for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
            const key = `${groupId}-${dayOfWeek}`;
            tvSnap[key] = toggleValues[key];
          }
          setBulkEntrySnapshot({
            row: JSON.parse(JSON.stringify(currentRow)),
            toggleValues: tvSnap,
          });
        }
        // Keep editingGroupId set so edit-mode buttons stay rendered
        // (preserves the anchor element for the bulk popover)
      } else {
        // First time entering bulk edit: save original snapshot
        if (currentRow) {
          setRowSnapshots((prev) => ({
            ...prev,
            [groupId]: JSON.parse(JSON.stringify(currentRow)),
          }));
          const tvSnap: { [key: string]: string | undefined } = {};
          for (let dayOfWeek = 1; dayOfWeek <= 5; dayOfWeek++) {
            const key = `${groupId}-${dayOfWeek}`;
            tvSnap[key] = toggleValues[key];
          }
          setToggleValuesSnapshots((prev) => ({ ...prev, [groupId]: tvSnap }));
        }
        setBulkEntrySnapshot(null);
      }

      setBulkEditGroupId(groupId);
      setBulkAnchorEl(anchorEl);
      setBulkSelectedDays(new Set());
      setBulkAvailabilityStatus("unavailable");
      setBulkToggleValue("Nicht verfügbar");
      setBulkDraft({ startTime: null, endTime: null });
      setBulkTimeBlocks([]);
      setBulkTimeError(null);
      setBulkOverlapError(null);
      setBulkConfirmDiscard(false);
      setBulkApplyMode("replace");
    };

    const handleBulkDayToggle = (dayOfWeek: number) => {
      setBulkSelectedDays((prev) => {
        const next = new Set(prev);
        if (next.has(dayOfWeek)) {
          next.delete(dayOfWeek);
        } else {
          next.add(dayOfWeek);
        }
        return next;
      });
    };

    const handleBulkSelectAll = () => {
      setBulkSelectedDays((prev) => {
        if (prev.size === 5) return new Set();
        return new Set([1, 2, 3, 4, 5]);
      });
    };

    // Collect existing time blocks on selected days for overlap checks in "add" mode
    const getExistingBlocksForBulkDays = (): DayData[] => {
      if (bulkApplyMode !== "add" || !bulkEditGroupId || bulkSelectedDays.size === 0) return [];
      const currentRow = rows.find((r) => r.groupId === bulkEditGroupId);
      if (!currentRow) return [];
      return currentRow.dayData.filter(
        (d) => bulkSelectedDays.has(d.dayOfWeek) && d.startTime && d.endTime,
      );
    };

    const handleBulkTimeChange = (value: string, timeType: "startTime" | "endTime") => {
      const newDraft = { ...bulkDraft, [timeType]: value };
      setBulkDraft(newDraft);

      const error = validateTimeInput(newDraft.startTime, newDraft.endTime);
      setBulkTimeError(error);

      if (!error && newDraft.startTime && newDraft.endTime) {
        const existingBlocks = getExistingBlocksForBulkDays();
        const allBlocks = [...(bulkTimeBlocks as any), ...existingBlocks];
        const overlapResult = checkTimeBlockOverlap(
          newDraft.startTime,
          newDraft.endTime,
          allBlocks,
        );
        setBulkOverlapError(overlapResult.hasOverlap ? overlapResult.message || null : null);
      } else {
        setBulkOverlapError(null);
      }
    };

    const handleBulkTimeBlur = (timeType: "startTime" | "endTime") => {
      const currentValue = bulkDraft[timeType];
      if (currentValue && /^\d{1,2}$/.test(currentValue)) {
        const hours = parseInt(currentValue, 10);
        if (hours >= 0 && hours <= 23) {
          handleBulkTimeChange(`${hours.toString().padStart(2, "0")}:00`, timeType);
        }
      }
    };

    const handleBulkAddTimeBlock = () => {
      const start = bulkDraft.startTime;
      const end = bulkDraft.endTime;
      if (!start || !end || bulkTimeError) return;

      const existingBlocks = getExistingBlocksForBulkDays();
      const allBlocks = [...(bulkTimeBlocks as any), ...existingBlocks];
      const overlapCheck = checkTimeBlockOverlap(start, end, allBlocks);
      if (overlapCheck.hasOverlap) {
        const merged = mergeTimeBlocks(start, end, allBlocks, "", 0);
        setBulkTimeBlocks(
          merged.map((b) => ({
            uid: uuidv4(),
            startTime:
              typeof b.startTime === "string" ? b.startTime : new Date(b.startTime!).toISOString(),
            endTime: typeof b.endTime === "string" ? b.endTime : new Date(b.endTime!).toISOString(),
          })),
        );
      } else {
        setBulkTimeBlocks((prev) => [
          ...prev,
          {
            uid: uuidv4(),
            startTime: parseTime(start, true) as string,
            endTime: parseTime(end, true) as string,
          },
        ]);
      }
      setBulkDraft({ startTime: null, endTime: null });
      setBulkTimeError(null);
      setBulkOverlapError(null);
    };

    const handleBulkDeleteTimeBlock = (uid: string) => {
      setBulkTimeBlocks((prev) => prev.filter((b) => b.uid !== uid));
    };

    const handleBulkClearAllTimeBlocks = () => {
      setBulkTimeBlocks([]);
      setBulkDraft({ startTime: null, endTime: null });
      setBulkTimeError(null);
      setBulkOverlapError(null);
    };

    const handleBulkApply = () => {
      if (!bulkEditGroupId || bulkSelectedDays.size === 0) return;

      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.groupId !== bulkEditGroupId) return row;

          let newDayData = [...row.dayData];

          bulkSelectedDays.forEach((dayOfWeek) => {
            // "unavailable" always replaces; "partial" respects bulkApplyMode
            const useReplace =
              bulkAvailabilityStatus === "unavailable" || bulkApplyMode === "replace";

            if (useReplace) {
              // Remove all existing blocks for this day
              newDayData = newDayData.filter((d) => d.dayOfWeek !== dayOfWeek);
            }

            if (bulkAvailabilityStatus === "unavailable") {
              newDayData.push({
                uid: uuidv4(),
                id: null,
                groupId: bulkEditGroupId,
                dayOfWeek,
                selected: true,
                availabilityStatus: "unavailable",
                startTime: null,
                endTime: null,
              });
            } else {
              // partial — add time blocks
              if (bulkTimeBlocks.length > 0) {
                if (useReplace) {
                  // Replace mode: just push new blocks
                  bulkTimeBlocks.forEach((block) => {
                    newDayData.push({
                      uid: uuidv4(),
                      id: null,
                      groupId: bulkEditGroupId,
                      dayOfWeek,
                      selected: true,
                      availabilityStatus: "partial",
                      startTime: block.startTime,
                      endTime: block.endTime,
                    });
                  });
                } else {
                  // Add mode: merge new blocks with existing ones
                  const existingBlocks = newDayData.filter(
                    (d) => d.dayOfWeek === dayOfWeek && d.startTime && d.endTime,
                  );

                  bulkTimeBlocks.forEach((block) => {
                    const startStr = dayjs(block.startTime).format("HH:mm");
                    const endStr = dayjs(block.endTime).format("HH:mm");
                    const overlapCheck = checkTimeBlockOverlap(startStr, endStr, existingBlocks);

                    if (overlapCheck.hasOverlap) {
                      // Merge: remove overlapping blocks, add merged result
                      const merged = mergeTimeBlocks(
                        startStr,
                        endStr,
                        existingBlocks,
                        bulkEditGroupId,
                        dayOfWeek,
                      );
                      // Remove all existing blocks for this day from newDayData
                      newDayData = newDayData.filter((d) => d.dayOfWeek !== dayOfWeek);
                      // Push merged blocks
                      merged.forEach((m) => {
                        newDayData.push({
                          ...m,
                          uid: m.uid || uuidv4(),
                          id: null,
                          groupId: bulkEditGroupId,
                          dayOfWeek,
                          selected: true,
                          availabilityStatus: "partial",
                        });
                      });
                      // Update existingBlocks reference for next iteration
                      existingBlocks.length = 0;
                      existingBlocks.push(
                        ...newDayData.filter(
                          (d) => d.dayOfWeek === dayOfWeek && d.startTime && d.endTime,
                        ),
                      );
                    } else {
                      // No overlap: simply append
                      const newBlock: DayData = {
                        uid: uuidv4(),
                        id: null,
                        groupId: bulkEditGroupId,
                        dayOfWeek,
                        selected: true,
                        availabilityStatus: "partial",
                        startTime: block.startTime,
                        endTime: block.endTime,
                      };
                      newDayData.push(newBlock);
                      existingBlocks.push(newBlock);
                    }
                  });
                }
              } else if (useReplace) {
                // partial with no blocks yet — add empty partial entry (only in replace mode)
                newDayData.push({
                  uid: uuidv4(),
                  id: null,
                  groupId: bulkEditGroupId,
                  dayOfWeek,
                  selected: true,
                  availabilityStatus: "partial",
                  startTime: null,
                  endTime: null,
                });
              }
            }
          });

          return { ...row, dayData: newDayData };
        }),
      );

      // Set toggle values for the affected days
      bulkSelectedDays.forEach((dayOfWeek) => {
        const key = `${bulkEditGroupId}-${dayOfWeek}`;
        if (bulkAvailabilityStatus === "unavailable") {
          setToggleValues((prev) => ({ ...prev, [key]: "Nicht verfügbar" }));
        } else {
          setToggleValues((prev) => ({ ...prev, [key]: bulkToggleValue }));
        }
      });

      // Transition to single-edit mode for review
      setEditingGroupId(bulkEditGroupId);
      setEditEnteredFromBulk(true);
      // Close bulk popover
      setBulkEditGroupId(null);
      setBulkAnchorEl(null);
    };

    const hasBulkChanges = (): boolean => {
      return bulkSelectedDays.size > 0 || bulkTimeBlocks.length > 0;
    };

    const handleBulkCancel = () => {
      // Guard: if there are unsaved changes, show inline confirmation first
      if (bulkEditGroupId && hasBulkChanges() && !bulkConfirmDiscard) {
        setBulkConfirmDiscard(true);
        return;
      }

      if (bulkEditGroupId) {
        if (bulkEntrySnapshot) {
          // Re-entered from edit mode: restore to pre-second-bulk state and go back to edit mode
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.groupId === bulkEditGroupId
                ? JSON.parse(JSON.stringify(bulkEntrySnapshot.row))
                : row,
            ),
          );
          setToggleValues((prev) => ({ ...prev, ...bulkEntrySnapshot.toggleValues }));
          setEditingGroupId(bulkEditGroupId);
          setBulkEntrySnapshot(null);
        } else {
          // First time bulk edit: restore from original snapshot
          const snapshot = rowSnapshots[bulkEditGroupId];
          if (snapshot) {
            setRows((prevRows) =>
              prevRows.map((row) =>
                row.groupId === bulkEditGroupId ? JSON.parse(JSON.stringify(snapshot)) : row,
              ),
            );
            setRowSnapshots((prev) => {
              const { [bulkEditGroupId]: _, ...rest } = prev;
              return rest;
            });
          }
        }
      }
      setBulkEditGroupId(null);
      setBulkAnchorEl(null);
      setBulkSelectedDays(new Set());
      setBulkDraft({ startTime: null, endTime: null });
      setBulkTimeBlocks([]);
      setBulkTimeError(null);
      setBulkOverlapError(null);
      setBulkConfirmDiscard(false);
      setBulkApplyMode("replace");
    };

    const handleAddTimeBlockClick = (groupId: string, dayOfWeek: number) => {
      const draftData = draft[`${groupId}-${dayOfWeek}`];
      const start = draftData?.startTime;
      const end = draftData?.endTime;
      const dayKey = `${groupId}-${dayOfWeek}`;

      if (start && end && !timeValidationErrors[dayKey]) {
        const currentRow = rows.find((r) => r.groupId === groupId);
        const existingBlocks =
          currentRow?.dayData.filter(
            (block) => block.dayOfWeek === dayOfWeek && block.startTime && block.endTime,
          ) || [];

        // Check for overlaps directly here to be sure
        const overlapCheck = checkTimeBlockOverlap(start, end, existingBlocks);

        // If there's an overlap, merge the time blocks
        if (overlapCheck.hasOverlap) {
          const mergedBlocks = mergeTimeBlocks(start, end, existingBlocks, groupId, dayOfWeek);

          // Update the rows with merged blocks
          setRows((prevRows) =>
            prevRows.map((r) =>
              r.groupId === groupId
                ? {
                    ...r,
                    dayData: [
                      // Keep all blocks for other days and empty blocks for this day
                      ...r.dayData.filter(
                        (d) => d.dayOfWeek !== dayOfWeek || (!d.startTime && !d.endTime),
                      ),
                      // Add merged blocks for this day
                      ...mergedBlocks.map((block) => ({
                        ...block,
                        uid: uuidv4(), // Generate new UID for merged blocks
                      })),
                    ],
                  }
                : r,
            ),
          );
        } else {
          // No overlap, just add normally
          addTimeBlock(groupId, dayOfWeek, start, end);
        }

        setDraft((p) => ({
          ...p,
          [dayKey]: {
            startTime: null,
            endTime: null,
          },
        }));
        // Clear validation and overlap errors after successful add
        setTimeValidationErrors((prev) => {
          const { [dayKey]: _, ...rest } = prev;
          return rest;
        });
        setOverlapErrors((prev) => {
          const { [dayKey]: _, ...rest } = prev;
          return rest;
        });
      }
    };

    const transformInputData = () => {
      const transformedData: PatientAvailabilityDto[] = [];
      rows.forEach((row) => {
        // Generate a single replacement groupId per row for gap rows
        const effectiveGroupId = row.groupId.startsWith("gap-") ? uuidv4() : row.groupId;
        // Track which days have already been processed for 'Verfügbar' partials
        const processedDays = new Set<number>();
        row.dayData.forEach((day: any) => {
          if (day.selected && day.availabilityStatus !== "available") {
            const toggleKey = `${row.groupId}-${day.dayOfWeek}`;
            const toggleValue = toggleValues[toggleKey];

            // Only process each day once for 'Verfügbar' partials
            if (
              toggleValue === "Verfügbar" &&
              day.availabilityStatus === "partial" &&
              day.startTime &&
              day.endTime &&
              !processedDays.has(day.dayOfWeek)
            ) {
              const dayBlocks = row.dayData.filter(
                (d) => d.dayOfWeek === day.dayOfWeek && d.startTime && d.endTime,
              );
              // console.log("Entered verfügbar times for day", day.dayOfWeek, ":", dayBlocks);
              const unavailabilityBlocks = transformAvailabilityToUnavailability(
                dayBlocks,
                day.dayOfWeek,
                effectiveGroupId,
                row.validityStart,
                row.validityEnd,
              );
              // console.log(
                // "Transformed nicht verfügbar for day",
                // day.dayOfWeek,
                // ":",
                // unavailabilityBlocks,
              // );
              transformedData.push(...unavailabilityBlocks);
              processedDays.add(day.dayOfWeek);
              return; // Skip regular processing for this day
            }

            // Only add non-'Verfügbar' or non-partial entries
            if (!(toggleValue === "Verfügbar" && day.availabilityStatus === "partial")) {
              let startTime: string | null = null;
              let endTime: string | null = null;

              if (day.availabilityStatus === "partial") {
                // For partial availability, use user-entered times (already in ISO format)
                startTime = day.startTime;
                endTime = day.endTime;
                if (!startTime || !endTime) {
                  // Skip if no times entered for partial
                  return;
                }
              } else if (day.availabilityStatus === "unavailable") {
                // For unavailable, save 7:00-20:00
                const startOfDay = dayjs().hour(7).minute(0).second(0).millisecond(0);
                const endOfDay = dayjs().hour(20).minute(0).second(0).millisecond(0);

                startTime = startOfDay.toISOString();
                endTime = endOfDay.toISOString();
              }

              const transformedObject: any = {
                id: day.id ? day.id : undefined,
                groupId: effectiveGroupId,
                dayOfWeek: day.dayOfWeek,
                startTime: startTime,
                endTime: endTime,
                validityStart: row.validityStart ? dayjs(row.validityStart).toISOString() : null,
                validityEnd: row.validityEnd ? dayjs(row.validityEnd).toISOString() : null,
              };

              transformedData.push(transformedObject);
            }
          }
        });
      });
      return transformedData;
    };

    // console.log("IIOIOOO", rows);
    return (
      <ThemeProvider theme={localTableTheme}>
        <TableContainer sx={{ overflowX: "hidden" }}>
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.groupId}
                    sx={{
                      borderLeft:
                        row.groupId === aiDraftGroupId
                          ? "3px solid #f5a623"
                          : editingGroupId === row.groupId || bulkEditGroupId === row.groupId
                            ? "3px solid #0f68c1"
                            : "none",
                      boxShadow:
                        row.groupId === aiDraftGroupId
                          ? "0px 0px 8px 4px rgba(245, 166, 35, 0.25)"
                          : editingGroupId === row.groupId || bulkEditGroupId === row.groupId
                            ? "0px 0px 6px 4px #7c7b7b25"
                            : "none",
                      backgroundColor:
                        row.groupId === aiDraftGroupId
                          ? "rgba(255, 215, 0, 0.15)"
                          : editingGroupId === row.groupId || bulkEditGroupId === row.groupId
                            ? "#c5e2fd22"
                            : "transparent",
                      pointerEvents:
                        (editingGroupId && editingGroupId !== row.groupId) ||
                        (bulkEditGroupId && bulkEditGroupId !== row.groupId)
                          ? "none"
                          : "auto",
                      transition:
                        "box-shadow ease, background-color ease, border-left ease, opacity 0.2s ease-in-out",
                      position: "relative",
                      cursor: !editingGroupId && !bulkEditGroupId ? "pointer" : "default",
                      height: 98,
                      minHeight: 98,
                      ...(row.groupId === aiDraftGroupId && {
                        animation: "aiRowPulse 2s ease-in-out 1",
                        "@keyframes aiRowPulse": {
                          "0%": { backgroundColor: "rgba(255, 215, 0, 0.4)" },
                          "100%": { backgroundColor: "rgba(255, 215, 0, 0.15)" },
                        },
                      }),
                    }}
                    onClick={() => {
                      if (!editingGroupId && !bulkEditGroupId) {
                        handleEditRow(row.groupId);
                      }
                    }}
                  >
                    {daysOfWeek.map((day, index) => {
                      const dayBlocks = row.dayData.filter((d) => d.dayOfWeek === index + 1);
                      const dayData = dayBlocks.length > 0 ? dayBlocks[0] : null;
                      // console.log("dayData", day, dayBlocks);

                      const weekdayData = {
                        availability: dayData ? getAvailabilityStatus(dayData) : "available",
                        startTime: dayData?.startTime || "",
                        selected: dayData?.selected || false,
                      };

                      return (
                        <React.Fragment key={index}>
                          <TableCell
                            sx={{
                              position: "relative",
                              margin: 0,
                              verticalAlign: "top",
                              height: "30px !important",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <WeekdayAvailability
                                dayData={weekdayData}
                                weekDay={day}
                                onChange={
                                  editingGroupId === row.groupId
                                    ? (newStatus, anchorEl) =>
                                        handleAvailabilityChange(
                                          row.groupId,
                                          index + 1,
                                          newStatus,
                                          anchorEl,
                                        )
                                    : () => {}
                                }
                                disabled={editingGroupId !== row.groupId}
                                unavailableTooltip={
                                  editingGroupId !== row.groupId &&
                                  weekdayData.availability === "partial"
                                    ? generateUnavailableTimeBlocksTooltip(dayBlocks)
                                    : undefined
                                }
                              />
                              {/* Show time blocks below icon */}
                              {weekdayData.availability === "partial" &&
                                dayBlocks.some((b) => b.startTime) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      justifyContent: "center",
                                      alignSelf: "stretch",
                                      gap: "0.75px",
                                      mt: 0,
                                      px: 0,
                                      maxHeight: "50px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {dayBlocks
                                      .filter((b) => b.startTime && b.endTime)
                                      .sort(
                                        (a, b) =>
                                          new Date(a.startTime!).getTime() -
                                          new Date(b.startTime!).getTime(),
                                      )
                                      .map((block) => {
                                        const toggleKey = `${row.groupId}-${index + 1}`;
                                        const toggleVal =
                                          toggleValues[toggleKey] || "Nicht verfügbar";
                                        const isVerfügbar = toggleVal === "Verfügbar";
                                        return (
                                          <Chip
                                            key={block.uid}
                                            icon={isVerfügbar ? <Check sx={{ fontSize: "0.55rem" }} /> : <Clear sx={{ fontSize: "0.55rem" }} />}
                                            label={`${formatTime(block.startTime!)}-${formatTime(block.endTime!)}`}
                                            size="small"
                                            variant="filled"
                                            sx={{
                                              backgroundColor: isVerfügbar
                                                ? "rgba(40, 181, 115, 0.08)"
                                                : "rgba(211, 47, 47, 0.08)",
                                              border: block.id === null
                                                ? isVerfügbar
                                                  ? "1.5px dashed rgba(30, 140, 90, 1)"
                                                  : "1.5px dashed rgba(180, 40, 40, 1)"
                                                : isVerfügbar
                                                  ? "1px solid rgba(40, 181, 115, 0.3)"
                                                  : "1px solid rgba(211, 47, 47, 0.3)",
                                              height: "auto",
                                              "& .MuiChip-icon": {
                                                fontSize: "0.55rem",
                                                ml: 0.3,
                                                mr: -0.3,
                                                color: isVerfügbar ? "success.main" : "error.main",
                                              },
                                              "& .MuiChip-label": {
                                                fontSize: "0.53rem",
                                                px: 0.2,
                                                py: 0.1,
                                              },
                                            }}
                                          />
                                        );
                                      })}
                                  </Box>
                                )}
                              {/* {dayData?.selected && ( */}
                              <>
                                <Box sx={{ direction: "column" }}>
                                  <Popover
                                    open={
                                      editingGroupId === row.groupId &&
                                      Boolean(
                                        popoverOpen[row.groupId] &&
                                        popoverOpen[row.groupId][index + 1],
                                      )
                                    }
                                    anchorEl={
                                      popoverOpen[row.groupId] &&
                                      popoverOpen[row.groupId][index + 1]
                                    }
                                    onClose={() => handlePopoverClose(row.groupId, index + 1)}
                                    anchorOrigin={{
                                      vertical: "bottom",
                                      horizontal: "left",
                                    }}
                                    transformOrigin={{
                                      vertical: "top",
                                      horizontal: "left",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        direction: "row",
                                        border: "5px solid #ccc",
                                        width: 400,
                                        p: 1,
                                        pt: 0,
                                        pb: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <CustomInput
                                        sx={{
                                          margin: "0 4px 4px 4px",
                                          fontWeight: "bold",
                                          width: 375,
                                          flexDirection: "column",
                                          mb: 1,
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            mb: 2,
                                            mt: 1,
                                            ml: 0,
                                            p: 0,
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "flex-start",
                                            alignItems: "flex-start",
                                          }}
                                        >
                                          <PatientAvailabilityToggle
                                            value={toggleValues[`${row.groupId}-${index + 1}`]}
                                            onChange={(val) =>
                                              setToggleValues((prev) => ({
                                                ...prev,
                                                [`${row.groupId}-${index + 1}`]: val,
                                              }))
                                            }
                                            disabled={
                                              dayBlocks.some((block) => block.id !== null) ||
                                              toggleForceDisabled[`${row.groupId}-${index + 1}`]
                                            }
                                          />
                                          <Tooltip
                                            title="Zurücksetzen für diesen Tag — alte Einträge werden nach dem Speichern ersetzt."
                                            placement="top"
                                            disableInteractive
                                          >
                                            <IconButton
                                              onClick={() => handleReset(row.groupId, index + 1)}
                                              size="large"
                                              sx={{ m: 0, p: 0, mr: 1 }}
                                            >
                                              <RestartAlt />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                            ml: 0,
                                            mb: 0,
                                          }}
                                        >
                                          <MobiliTime
                                            label="von"
                                            value={
                                              draft[`${row.groupId}-${index + 1}`]?.startTime || ""
                                            }
                                            onChange={(value) =>
                                              handleTimeChange(
                                                value,
                                                row.groupId,
                                                index + 1,
                                                "startTime",
                                              )
                                            }
                                            onBlur={() =>
                                              handleTimeBlur(row.groupId, index + 1, "startTime")
                                            }
                                            size="small"
                                            sx={{ mt: 0, mr: 2 }}
                                            InputLabelProps={{ shrink: true }}
                                            error={
                                              !!timeValidationErrors[`${row.groupId}-${index + 1}`]
                                            }
                                          />
                                          <MobiliTime
                                            label="bis"
                                            value={
                                              draft[`${row.groupId}-${index + 1}`]?.endTime || ""
                                            }
                                            onChange={(value) =>
                                              handleTimeChange(
                                                value,
                                                row.groupId,
                                                index + 1,
                                                "endTime",
                                              )
                                            }
                                            onBlur={() =>
                                              handleTimeBlur(row.groupId, index + 1, "endTime")
                                            }
                                            size="small"
                                            sx={{ mt: 0 }}
                                            InputLabelProps={{ shrink: true }}
                                            error={
                                              !!timeValidationErrors[`${row.groupId}-${index + 1}`]
                                            }
                                          />
                                          <Button
                                            size="small"
                                            sx={{
                                              ml: 2,
                                              mr: 0,
                                              mt: 0,
                                              mb: 1,
                                              minWidth: 0,
                                              backgroundColor: mobiliTheme.mainHeader,
                                              color: mobiliTheme.palette.secondary.contrastText,
                                            }}
                                            onClick={() =>
                                              handleAddTimeBlockClick(row.groupId, index + 1)
                                            }
                                            disabled={
                                              !draft[`${row.groupId}-${index + 1}`]?.startTime ||
                                              !draft[`${row.groupId}-${index + 1}`]?.endTime ||
                                              !/^\d{1,2}:\d{2}$/.test(
                                                draft[`${row.groupId}-${index + 1}`]?.startTime ||
                                                  "",
                                              ) ||
                                              !/^\d{1,2}:\d{2}$/.test(
                                                draft[`${row.groupId}-${index + 1}`]?.endTime || "",
                                              ) ||
                                              !!timeValidationErrors[`${row.groupId}-${index + 1}`]
                                            }
                                          >
                                            hinzufügen
                                            {/* <Add fontSize="small" /> */}
                                          </Button>
                                          <Tooltip
                                            title="Entfernt alle Zeiträume unten"
                                            placement="top"
                                            disableInteractive
                                            slotProps={{
                                              popper: {
                                                modifiers: [
                                                  {
                                                    name: "offset",
                                                    options: {
                                                      offset: [0, -10],
                                                    },
                                                  },
                                                ],
                                              },
                                            }}
                                          >
                                            <Button
                                              variant="text"
                                              size="small"
                                              // disableRipple
                                              onClick={() => handleClearAll(row.groupId, index + 1)}
                                              sx={{
                                                m: 0,
                                                ml: 2.75,
                                                mb: 1,
                                                p: 0,
                                                minWidth: 0,
                                                background: "none !important",
                                                border: "none",
                                                boxShadow: "none",
                                                textDecoration: "underline",
                                                textDecorationColor: "#cc0000",
                                                textTransform: "none",
                                                fontSize: 11,
                                                color: "#cc0000 !important",
                                                "&:hover": {
                                                  backgroundColor: "transparent !important",
                                                  background: "none !important",
                                                  border: "none",
                                                  boxShadow: "none",
                                                  color: "#ff4545ff !important",
                                                  textDecoration: "underline",
                                                  textDecorationColor: "#ff4545ff",
                                                },
                                              }}
                                            >
                                              Alle löschen
                                            </Button>
                                          </Tooltip>
                                        </Box>
                                        {timeValidationErrors[`${row.groupId}-${index + 1}`] && (
                                          <Box
                                            sx={{
                                              fontStyle: "italic",
                                              fontSize: "0.7rem",
                                              mt: 0.5,
                                              ml: 0,
                                            }}
                                          >
                                            <span style={{ fontStyle: "normal", fontSize: 14 }}>
                                              ❗
                                            </span>
                                            <span style={{ fontStyle: "italic" }}>
                                              {timeValidationErrors[`${row.groupId}-${index + 1}`]}
                                            </span>
                                          </Box>
                                        )}
                                        {overlapErrors[`${row.groupId}-${index + 1}`] && (
                                          <Box sx={{ fontSize: "0.7rem", mt: 0.5, ml: 0 }}>
                                            <span style={{ fontStyle: "normal", fontSize: 14 }}>
                                              ⚠️
                                            </span>
                                            <span style={{ fontStyle: "italic" }}>
                                              {overlapErrors[`${row.groupId}-${index + 1}`]}
                                            </span>
                                            <br />
                                            <span
                                              style={{
                                                fontStyle: "italic",
                                                fontWeight: "normal",
                                                color: "#666",
                                                fontSize: "0.65rem",
                                              }}
                                            >
                                              Klicken Sie auf "hinzufügen", um die Zeiträume
                                              automatisch zusammenzufassen.
                                            </span>
                                          </Box>
                                        )}
                                      </CustomInput>
                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: "repeat(2, 1fr)",
                                          gap: 1,
                                          ml: 1,
                                          mb: 1.5,
                                          pb: 0,
                                        }}
                                      >
                                        {dayBlocks
                                          .filter((block) => block.startTime)
                                          .sort((a, b) =>
                                            new Date(a.startTime!)
                                              .toLocaleTimeString()
                                              .localeCompare(
                                                new Date(b.startTime!).toLocaleTimeString(),
                                              ),
                                          )
                                          .map((block) => {
                                            const key = `${row.groupId}-${index + 1}`;
                                            const toggleValue =
                                              toggleValues[key] || "Nicht verfügbar";
                                            const isUnsaved = block.id === null;
                                            const isVerfügbar = toggleValue === "Verfügbar";
                                            const chipBg = isVerfügbar
                                              ? "rgba(40, 181, 115, 0.2)"
                                              : "rgba(255,0,0,0.2)";
                                            return (
                                              <Chip
                                                key={block.uid}
                                                label={
                                                  block.startTime
                                                    ? `${formatTime(block.startTime)}${
                                                        block.endTime
                                                          ? ` - ${formatTime(block.endTime)}`
                                                          : ""
                                                      }`
                                                    : ""
                                                }
                                                variant="filled"
                                                sx={{
                                                  backgroundColor: chipBg,
                                                  border: isUnsaved
                                                    ? isVerfügbar
                                                      ? "1.5px dashed rgba(30, 140, 90, 1)"
                                                      : "1.5px dashed rgba(180, 40, 40, 1)"
                                                    : "1px solid #bbb",
                                                }}
                                                onDelete={() =>
                                                  deleteTimeBlock(row.groupId, block.uid)
                                                }
                                              />
                                            );
                                          })}
                                      </Box>
                                      <Box sx={{ display: "flex", justifyContent: "flex-end", alignSelf: "stretch", px: 1, pb: 1 }}>
                                        <Button
                                          size="small"
                                          variant="text"
                                          startIcon={<CancelOutlined sx={{ fontSize: 16 }} />}
                                          onClick={() => handlePopoverClose(row.groupId, index + 1)}
                                          sx={{
                                            textTransform: "none",
                                            fontSize: 11,
                                            color: "text.secondary",
                                          }}
                                        >
                                          Schließen
                                        </Button>
                                      </Box>
                                    </Box>
                                  </Popover>
                                </Box>
                              </>
                              {/* )} */}
                            </Box>
                          </TableCell>
                        </React.Fragment>
                      );
                    })}

                    <TableCell sx={{ width: 105 }}>
                      <CustomInput
                        sx={{
                          fontWeight: "bold",
                          paddingLeft: "2px",
                          position: "absolute",
                          top: 0,
                        }}
                      >
                        Gültigkeit
                      </CustomInput>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: 1,
                          mt: 1.5,
                        }}
                      >
                        {editingGroupId === row.groupId ? (
                          <>
                            <MobiliDate
                              // label="von"
                              value={formatDateForMobiliDate(row.validityStart)}
                              onChange={(value) =>
                                handleValidityChange(row.groupId, "validityStart", value)
                              }
                              placeholder="ab"
                              size="small"
                              yearFormat="2-digit"
                              InputLabelProps={{ shrink: true }}
                              error={!!dateValidityValidationErrors[row.groupId]}
                              sx={{
                                width: 105,
                                ".MuiInputBase-root": {
                                  height: 30,
                                  padding: "5px",
                                  margin: "0px",
                                  fontSize: "15px",
                                  borderColor: dateValidityValidationErrors[row.groupId]
                                    ? "error.main"
                                    : undefined,
                                },
                              }}
                              margin="none"
                              showFormatOnFocus
                              // disabled={editingGroupId !== row.groupId}
                            />
                            <MobiliDate
                              // label="bis"
                              value={formatDateForMobiliDate(row.validityEnd)}
                              onChange={(value) =>
                                handleValidityChange(row.groupId, "validityEnd", value)
                              }
                              placeholder="bis"
                              size="small"
                              yearFormat="2-digit"
                              InputLabelProps={{ shrink: true }}
                              error={!!dateValidityValidationErrors[row.groupId]}
                              sx={{
                                width: 105,
                                ".MuiInputBase-root": {
                                  height: 30,
                                  padding: "5px",
                                  margin: "0px",
                                  fontSize: "15px",
                                  borderColor: dateValidityValidationErrors[row.groupId]
                                    ? "error.main"
                                    : undefined,
                                },
                              }}
                              margin="none"
                              showFormatOnFocus
                              // disabled={editingGroupId !== row.groupId}
                            />
                          </>
                        ) : row.validityStart || row.validityEnd ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 0,
                              width: 105,
                            }}
                          >
                            {row.validityStart && row.validityEnd ? (
                              <>
                                <Typography
                                  sx={{
                                    fontSize: 15,
                                    fontStyle: "italic",
                                    m: 0,
                                    p: 0,
                                    lineHeight: 1.3,
                                    color: "text.secondary",
                                    textAlign: "center",
                                  }}
                                >
                                  {formatDateForDisplay(row.validityStart)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontStyle: "italic",
                                    m: 0,
                                    p: 0,
                                    lineHeight: 1.3,
                                    color: "text.secondary",
                                    textAlign: "center",
                                  }}
                                >
                                  →
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 15,
                                    fontStyle: "italic",
                                    m: 0,
                                    p: 0,
                                    lineHeight: 1.3,
                                    color: "text.secondary",
                                    textAlign: "center",
                                  }}
                                >
                                  {formatDateForDisplay(row.validityEnd)}
                                </Typography>
                              </>
                            ) : row.validityStart ? (
                              <Typography
                                sx={{
                                  fontSize: 15,
                                  fontStyle: "italic",
                                  m: 0,
                                  p: 0,
                                  lineHeight: 1.3,
                                  color: "text.secondary",
                                  textAlign: "center",
                                }}
                              >
                                ab {formatDateForDisplay(row.validityStart)}
                              </Typography>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: 15,
                                  fontStyle: "italic",
                                  m: 0,
                                  p: 0,
                                  lineHeight: 1.3,
                                  color: "text.secondary",
                                  textAlign: "center",
                                }}
                              >
                                bis {formatDateForDisplay(row.validityEnd)}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: 105,
                              mb: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 16,
                                fontStyle: "italic",
                                m: 0,
                                p: 0,
                                color: "text.secondary",
                                wordWrap: "break-word",
                                textAlign: "center",
                              }}
                            >
                              allgemein gültig
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor:
                          editingGroupId === row.groupId || bulkEditGroupId === row.groupId
                            ? "rgb(253, 249, 202)"
                            : "none",
                        width: 40,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: editingGroupId === row.groupId ? 0.8 : 2.5,
                          height: "100%",
                        }}
                      >
                        {editingGroupId === row.groupId ? (
                          <>
                            <Tooltip title="Speichern" placement="right" disableInteractive>
                              <span>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveRow();
                                  }}
                                  disabled={!hasRowChanges(row.groupId) || isLoading}
                                  sx={{ padding: 0, margin: 0 }}
                                >
                                  {isLoading ? (
                                    <CircularProgress color="inherit" size={24} />
                                  ) : (
                                    <Save />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Sammelbearbeitung" placement="right" disableInteractive>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBulkEditRow(row.groupId, e.currentTarget);
                                }}
                                disabled={isLoading}
                                sx={{ padding: 0, margin: 0 }}
                              >
                                <EditNote />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Abbrechen" placement="right" disableInteractive>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelRow(row.groupId);
                                }}
                                disabled={isLoading}
                                sx={{ padding: 0, margin: 0 }}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Bearbeiten" placement="right" disableInteractive>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditRow(row.groupId);
                                }}
                                disabled={
                                  (!!editingGroupId && editingGroupId !== row.groupId) ||
                                  !!bulkEditGroupId
                                }
                                size="small"
                                sx={{ padding: 0, margin: 0 }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Löschen" placement="right" disableInteractive>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRow(row.groupId);
                                }}
                                disabled={
                                  (!!editingGroupId && editingGroupId !== row.groupId) ||
                                  !!bulkEditGroupId
                                }
                                size="small"
                                sx={{ padding: 0, margin: 0 }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </LocalizationProvider>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end" p={1} mt={1}>
          <Button
            startIcon={<Add />}
            onClick={handleAddRow}
            disabled={!!editingGroupId || !!bulkEditGroupId}
          >
            Hinzufügen
          </Button>
        </Box>

        {/* Bulk Edit Popover */}
        <Popover
          open={!!bulkEditGroupId && !!bulkAnchorEl}
          anchorEl={bulkAnchorEl}
          onClose={bulkConfirmDiscard ? undefined : handleBulkCancel}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box
            sx={{
              width: 420,
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              border: "5px solid rgba(0, 48, 89, 0.4)",
            }}
          >
            {bulkConfirmDiscard ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  py: 2,
                  px: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: "#476987",
                    textAlign: "center",
                  }}
                >
                  Änderungen verwerfen?
                </Typography>
                <Typography
                  sx={{ fontSize: "0.75rem", color: "text.secondary", textAlign: "center" }}
                >
                  Ihre Eingaben in der Sammelbearbeitung gehen verloren.
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
                  <Button variant="text" size="small" onClick={() => setBulkConfirmDiscard(false)}>
                    Zurück
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setBulkConfirmDiscard(true);
                      handleBulkCancel();
                    }}
                    sx={{
                      backgroundColor: "#cc0000",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#a00000" },
                    }}
                  >
                    Verwerfen
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {/* Header */}
                <Typography
                  sx={{ fontWeight: "bold", fontSize: "0.8rem", color: "#476987", mb: 0.5 }}
                >
                  Sammelbearbeitung der Verfügbarkeit
                </Typography>

                {/* Step 1: Day selection */}
                <Box
                  sx={{
                    backgroundColor: "#f7f9fb",
                    borderRadius: 1,
                    p: 1,
                    border: "1px solid #e0e4e8",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: "bold",
                      color: "#8a9bb0",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    Tage
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0,
                      flexWrap: "wrap",
                      m: 0,
                      p: 0,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={bulkSelectedDays.size === 5}
                          indeterminate={bulkSelectedDays.size > 0 && bulkSelectedDays.size < 5}
                          onChange={handleBulkSelectAll}
                          sx={{ p: 0.3, "&.MuiCheckbox-indeterminate": { color: "black" } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                          Alle
                        </Typography>
                      }
                      sx={{ mr: 1, ml: 0, gap: 0, "& .MuiFormControlLabel-label": { ml: 0 } }}
                    />
                    {daysOfWeek.map((day, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            size="small"
                            checked={bulkSelectedDays.has(idx + 1)}
                            onChange={() => handleBulkDayToggle(idx + 1)}
                            sx={{ p: 0.3 }}
                          />
                        }
                        label={<Typography sx={{ fontSize: "0.75rem" }}>{day}</Typography>}
                        sx={{ mr: 1, gap: 0, "& .MuiFormControlLabel-label": { ml: 0 } }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Step 2: Availability status selector */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 0.5 }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: "bold",
                      color: "#8a9bb0",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                      ml: 1,
                      alignSelf: "flex-start",
                    }}
                  >
                    Modus
                  </Typography>
                  <ToggleButtonGroup
                    value={bulkAvailabilityStatus}
                    exclusive
                    onChange={(_e, val) => {
                      if (val) setBulkAvailabilityStatus(val);
                    }}
                    size="small"
                    sx={{ alignSelf: "center" }}
                  >
                    <ToggleButton
                      value="unavailable"
                      sx={{ textTransform: "none", fontSize: 11, m: 0, p: 0.5 }}
                    >
                      <BlockOutlined sx={{ fontSize: 14, mr: 0.3 }} />
                      Ganztägig nicht verfügbar
                    </ToggleButton>
                    <ToggleButton
                      value="partial"
                      sx={{ textTransform: "none", fontSize: 11, m: 0, p: 0.5 }}
                    >
                      <AccessTime sx={{ fontSize: 14, mr: 0.3 }} />
                      Zeitraum angeben
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Step 3: Time configuration — only visible for partial */}
                {bulkAvailabilityStatus === "partial" && (
                  <Box
                    sx={{
                      backgroundColor: "#f7f9fb",
                      borderRadius: 1,
                      p: 1,
                      border: "1px solid #e0e4e8",
                      mt: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.8,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: "bold",
                          color: "#8a9bb0",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Zeitangaben
                      </Typography>
                      {/* Replace / Add mode — inline in the section header */}
                      {selectedDaysHaveExistingData && (
                        <RadioGroup
                          row
                          value={bulkApplyMode}
                          onChange={(_e, val) => setBulkApplyMode(val as "replace" | "add")}
                          sx={{ gap: 0 }}
                        >
                          <Tooltip
                            title={
                              bulkApplyMode === "replace"
                                ? "Bestehende Einträge werden überschrieben"
                                : ""
                            }
                            arrow
                            placement="top"
                            disableInteractive
                          >
                            <FormControlLabel
                              value="replace"
                              control={
                                <Radio
                                  size="small"
                                  sx={{ p: 0.2, backgroundColor: "transparent" }}
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                  <SwapHoriz sx={{ fontSize: 13 }} />
                                  <Typography sx={{ fontSize: "0.68rem" }}>Ersetzen</Typography>
                                </Box>
                              }
                              sx={{
                                mr: 1,
                                ml: 0,
                                gap: 0,
                                "& .MuiFormControlLabel-label": { ml: 0 },
                              }}
                            />
                          </Tooltip>
                          <Tooltip
                            title={
                              bulkApplyMode === "add"
                                ? "Neue Zeiträume werden zu bestehenden ergänzt"
                                : ""
                            }
                            arrow
                            placement="top"
                            disableInteractive
                          >
                            <FormControlLabel
                              value="add"
                              control={
                                <Radio
                                  size="small"
                                  sx={{ p: 0.2, backgroundColor: "transparent" }}
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                  <PlaylistAdd sx={{ fontSize: 13 }} />
                                  <Typography sx={{ fontSize: "0.68rem" }}>Hinzufügen</Typography>
                                </Box>
                              }
                              sx={{
                                mr: 0,
                                ml: 0,
                                gap: 0,
                                "& .MuiFormControlLabel-label": { ml: 0 },
                              }}
                            />
                          </Tooltip>
                        </RadioGroup>
                      )}
                    </Box>

                    <PatientAvailabilityToggle
                      value={bulkToggleValue}
                      onChange={(val) => setBulkToggleValue(val as "Verfügbar" | "Nicht verfügbar")}
                      disabled={bulkApplyMode === "add" && selectedDaysHaveExistingData}
                    />
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                      <MobiliTime
                        label="von"
                        value={bulkDraft.startTime || ""}
                        onChange={(value) => handleBulkTimeChange(value, "startTime")}
                        onBlur={() => handleBulkTimeBlur("startTime")}
                        size="small"
                        sx={{ mr: 2 }}
                        InputLabelProps={{ shrink: true }}
                        error={!!bulkTimeError}
                      />
                      <MobiliTime
                        label="bis"
                        value={bulkDraft.endTime || ""}
                        onChange={(value) => handleBulkTimeChange(value, "endTime")}
                        onBlur={() => handleBulkTimeBlur("endTime")}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        error={!!bulkTimeError}
                      />
                      <Button
                        size="small"
                        sx={{
                          ml: 2,
                          minWidth: 0,
                          backgroundColor: mobiliTheme.mainHeader,
                          color: mobiliTheme.palette.secondary.contrastText,
                        }}
                        onClick={handleBulkAddTimeBlock}
                        disabled={
                          !bulkDraft.startTime ||
                          !bulkDraft.endTime ||
                          !/^\d{1,2}:\d{2}$/.test(bulkDraft.startTime || "") ||
                          !/^\d{1,2}:\d{2}$/.test(bulkDraft.endTime || "") ||
                          !!bulkTimeError
                        }
                      >
                        hinzufügen
                      </Button>
                      {bulkTimeBlocks.length > 0 && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleBulkClearAllTimeBlocks}
                          sx={{
                            ml: 2,
                            p: 0,
                            minWidth: 0,
                            textDecoration: "underline",
                            textDecorationColor: "#cc0000",
                            textTransform: "none",
                            fontSize: 11,
                            color: "#cc0000 !important",
                            "&:hover": {
                              backgroundColor: "transparent !important",
                              background: "none !important",
                              border: "none",
                              boxShadow: "none",
                              color: "#ff4545ff !important",
                              textDecoration: "underline",
                              textDecorationColor: "#ff4545ff",
                            },
                          }}
                        >
                          Alle löschen
                        </Button>
                      )}
                    </Box>
                    {bulkTimeError && (
                      <Box sx={{ fontStyle: "italic", fontSize: "0.7rem", ml: 0 }}>
                        <span style={{ fontStyle: "normal", fontSize: 14 }}>❗</span>
                        <span style={{ fontStyle: "italic", fontWeight: "bold" }}>
                          {bulkTimeError}
                        </span>
                      </Box>
                    )}
                    {bulkOverlapError && (
                      <Box sx={{ fontSize: "0.7rem", ml: 0 }}>
                        <span style={{ fontStyle: "normal", fontSize: 14 }}>⚠️</span>
                        <span style={{ fontStyle: "italic", fontWeight: "bold" }}>
                          {bulkOverlapError}
                        </span>
                        <br />
                        <span style={{ fontStyle: "italic", color: "#666", fontSize: "0.65rem" }}>
                          Klicken Sie auf "hinzufügen", um die Zeiträume automatisch
                          zusammenzufassen.
                        </span>
                      </Box>
                    )}
                    {bulkTimeBlocks.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                        {bulkTimeBlocks
                          .sort((a, b) =>
                            new Date(a.startTime)
                              .toLocaleTimeString()
                              .localeCompare(new Date(b.startTime).toLocaleTimeString()),
                          )
                          .map((block) => {
                            const chipColor =
                              bulkToggleValue === "Verfügbar"
                                ? "rgba(40, 181, 115, 0.2)"
                                : "rgba(255,0,0,0.2)";
                            return (
                              <Chip
                                key={block.uid}
                                label={`${formatTime(block.startTime)} - ${formatTime(block.endTime)}`}
                                variant="filled"
                                size="small"
                                sx={{
                                  backgroundColor: chipColor,
                                  fontSize: 12,
                                  border: bulkToggleValue === "Verfügbar"
                                    ? "1.5px dashed rgba(30, 140, 90, 1)"
                                    : "1.5px dashed rgba(180, 40, 40, 1)",
                                }}
                                onDelete={() => handleBulkDeleteTimeBlock(block.uid)}
                              />
                            );
                          })}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Action buttons */}
                <Divider sx={{ mt: 1.5 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
                  <Button variant="text" size="small" onClick={handleBulkCancel}>
                    Abbrechen
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleBulkApply}
                    disabled={
                      bulkSelectedDays.size === 0 ||
                      (bulkAvailabilityStatus === "partial" && bulkTimeBlocks.length === 0)
                    }
                    sx={{
                      backgroundColor: "#003059",
                      color: mobiliTheme.palette.secondary.contrastText,
                    }}
                  >
                    Übernehmen
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Popover>
      </ThemeProvider>
    );
  },
);

export default PatientUnavailability;
